// Swedish texts. This file defines the keys: en.ts must have exactly the same shape (TypeScript checks it).
// Placeholders in {braces} are filled with `fill()` from ./context.

export const sv = {
  common: {
    openFullPage: 'Öppna hela sidan',
    pickItem: 'Välj en i listan för att se den här',
    demo: 'Demodata',
    back: 'Tillbaka',
    cancel: 'Avbryt',
    save: 'Spara',
    all: 'Alla',
    viewAll: 'Visa alla',
    loading: 'Laddar',
    errorTitle: 'Det gick inte att ladda',
    errorMessage: 'Något gick fel. Kontrollera din anslutning och försök igen.',
    retry: 'Försök igen',
    notFoundTitle: 'Sidan finns inte',
    notFoundText: 'Länken kan vara gammal, eller så har sidan flyttats.',
    goHome: 'Till startsidan',
    showingCount: 'Visar {shown} av {total}',
    showingRange: 'Visar {from}–{to} av {total}',
    previous: 'Föregående',
    next: 'Nästa',
    clearFilters: 'Rensa filter',
    noMatchesTitle: 'Inga träffar',
    yes: 'Ja',
    no: 'Nej',
    noMatchesText: 'Prova en annan sökning eller rensa filtren.',
  },
  layout: {
    nav: 'Huvudmeny',
    menu: 'Meny',
    openMenu: 'Öppna menyn',
    collapseSidebar: 'Fäll ihop sidomenyn',
    expandSidebar: 'Fäll ut sidomenyn',
    contextPanel: 'Detaljer',
    collapsePanel: 'Dölj detaljpanelen',
    expandPanel: 'Visa detaljpanelen',
    helpTitle: 'Behöver du hjälp?',
    helpText: 'Vår support hjälper dig gärna.',
    contactSupport: 'Kontakta support',
    search: 'Sök',
    searchPlaceholder: 'Sök …',
    closeSearch: 'Stäng sökningen',
    notificationsUnread: 'Aviseringar, {count} olästa',
    language: 'Språk',
    account: 'Kontomeny',
    settings: 'Inställningar',
    logOut: 'Logga ut',
  },
  areas: {
    client: 'Kund',
    provider: 'Utförare',
    admin: 'Admin',
  },
  nav: {
    billing: 'Intäkter',
    overview: 'Översikt',
    myJobs: 'Mina jobb',
    messages: 'Meddelanden',
    settings: 'Inställningar',
    findJobs: 'Hitta jobb',
    myProposals: 'Mina offerter',
    subscription: 'Abonnemang',
    companyProfile: 'Företagsprofil',
    review: 'Granska jobb',
    providers: 'Utförare',
    users: 'Användare',
    reports: 'Anmälningar',
    system: 'System',
    auditLog: 'Händelselogg',
  },
  auth: {
    panel: {
      title: 'Hemmets alla jobb, enkelt lösta',
      text: 'Lägg upp jobbet, få offerter från granskade hantverkare och välj den som passar dig.',
      points: [
        'Alla företag har kontrollerat organisationsnummer och F-skatt',
        'Gratis för dig som kund, utan förpliktelser',
        'Dina kontaktuppgifter delas först när du accepterar en offert',
      ],
      footnote: '© Hemmafixa North AB',
    },
    showcase: {
      company: 'Måleri Väst AB',
      label: 'Måla köksväggar',
      price: '18 500 kr',
      status: 'Accepterad',
      from: 'Erik, Måleri Väst',
      message: 'Hej! Jag kan komma förbi på torsdag och titta på väggarna.',
      progress: '3 offerter inkomna',
      caption: 'Granskade företag nära dig',
    },
    email: 'E-post',
    password: 'Lösenord',
    showPassword: 'Visa lösenord',
    hidePassword: 'Dölj lösenord',
    loginTitle: 'Logga in',
    loginDescription: 'Välkommen tillbaka till Hemmafixa.',
    login: 'Logga in',
    forgotLink: 'Glömt lösenordet?',
    noAccount: 'Inget konto? Du får ett när du lägger upp ditt första jobb på hemmafixa.se.',
    providerLink: 'Är du hantverkare? Registrera ditt företag',
    wrongCredentials: 'Fel e-post eller lösenord.',
    locked: 'För många misslyckade försök. Vänta en stund eller återställ ditt lösenord.',
    suspended: 'Kontot är avstängt. Kontakta Hemmafixa om du tror att det är fel.',
    sessionEnded: 'Du har loggats ut. Logga in igen.',
    emailInvalid: 'Ange en giltig e-postadress, t.ex. anna@exempel.se.',
    passwordRequired: 'Ange ditt lösenord.',
    forgotTitle: 'Glömt lösenordet?',
    forgotDescription: 'Ange din e-post så skickar vi en länk där du kan välja ett nytt lösenord.',
    sendLink: 'Skicka länk',
    sentTitle: 'Kolla din e-post',
    sentText:
      'Om det finns ett konto för {email} har vi skickat en länk. Den fungerar i en timme. Hittar du den inte, titta i skräpposten.',
    backToLogin: 'Tillbaka till inloggningen',
    setTitle: 'Välj ett lösenord',
    resetTitle: 'Välj ett nytt lösenord',
    forEmail: 'För {email}',
    newPassword: 'Nytt lösenord',
    newPasswordHint: 'Minst 10 tecken. En mening du minns är bra.',
    repeatPassword: 'Upprepa lösenordet',
    passwordTooShort: 'Använd minst 10 tecken.',
    passwordTooLong: 'Använd högst 200 tecken.',
    passwordsDontMatch: 'Lösenorden är inte likadana.',
    savePassword: 'Spara lösenord',
    checkingLink: 'Kontrollerar länken',
    linkInvalidTitle: 'Länken fungerar inte längre',
    linkInvalidText:
      'Länkar fungerar en gång och bara en begränsad tid. Be om en ny länk så skickar vi den direkt.',
    requestNewLink: 'Be om en ny länk',
    passwordSaved: 'Lösenordet är sparat. Du är inloggad.',
    genericError: 'Något gick fel. Försök igen om en stund.',
  },
  status: {
    job: {
      draft: 'Utkast',
      in_review: 'Granskas',
      needs_info: 'Behöver info',
      open: 'Öppet',
      provider_chosen: 'Utförare vald',
      rejected: 'Avvisat',
      cancelled: 'Avbrutet',
      expired: 'Utgånget',
    },
    proposal: {
      submitted: 'Skickad',
      accepted: 'Accepterad',
      declined: 'Avböjd',
      withdrawn: 'Återkallad',
      chosen: 'Vald',
      closed: 'Stängd',
    },
    provider: {
      onboarding: 'Påbörjad',
      pending: 'Väntar',
      verified: 'Verifierad',
      rejected: 'Avvisad',
    },
    user: {
      active: 'Aktiv',
      suspended: 'Avstängd',
    },
    subscription: {
      incomplete: 'Ej slutförd',
      trialing: 'Provperiod',
      active: 'Aktiv',
      past_due: 'Betalning misslyckad',
      canceled: 'Avslutad',
    },
    invoice: {
      draft: 'Utkast',
      open: 'Obetald',
      paid: 'Betald',
      uncollectible: 'Ej betald',
      void: 'Makulerad',
    },
  },
  categories: {
    malare: 'Målare',
    rormokare: 'Rörmokare',
    elektriker: 'Elektriker',
    stad: 'Städ',
    snickare: 'Snickare',
    tradgard: 'Trädgård',
  },
  wantedStart: {
    asap: 'Så snart som möjligt',
    within_month: 'Inom en månad',
    flexible: 'Flexibelt',
  },
  job: {
    description: 'Beskrivning',
    questions: 'Svar på assistentens frågor',
    noQuestions: 'Inga följdfrågor besvarades.',
    contactTitle: 'Kontaktuppgifter',
    contactEmail: 'E-post',
    contactPhone: 'Telefon',
    contactName: 'Kontaktperson',
    details: 'Detaljer',
    category: 'Kategori',
    mapLabel: 'Karta över var jobbet finns, {place}',
    mapField: 'Var på kartan',
    mapHint: 'Klicka på kartan där jobbet finns och dra nålen för att justera.',
    useMyLocation: 'Använd min plats',
    clearPin: 'Ta bort nålen',
    mapUnavailable:
      'Kartan är inte tillgänglig just nu. Postnumret räcker för att företagen ska hitta jobbet.',
    location: 'Plats',
    wantedStart: 'Önskad start',
    posted: 'Upplagt',
    client: 'Kund',
    notAnswered: 'Inte besvarad',
    fromAssistant: 'Assistenten',
    fromAdmin: 'Hemmafixa',
  },
  providerSignup: {
    title: 'Ta uppdrag via Hemmafixa',
    description: 'Registrera dig, berätta om ditt företag och få jobb i ditt område.',
    name: 'Ditt namn',
    phone: 'Mobilnummer',
    acceptTerms: 'Jag godkänner villkoren för utförare',
    submit: 'Registrera mig',
    sentTitle: 'Kolla din e-post',
    sentText: 'Vi har skickat nästa steg till {email}. Länken fungerar i 24 timmar.',
    signedInTitle: 'Fortsätt som utförare',
    signedInText: 'Du är inloggad som {name}. Vi lägger till ett företag på ditt konto.',
    continue: 'Fortsätt',
    notYou: 'Inte du? Logga ut',
    alreadyProvider: 'Du är redan utförare.',
    toProvider: 'Till din företagsprofil',
    nameInvalid: 'Skriv ditt namn',
    phoneInvalid: 'Skriv ett giltigt telefonnummer, till exempel 070-123 45 67',
    termsRequired: 'Godkänn villkoren för att registrera dig',
    failed: 'Det gick inte att registrera. Försök igen om en stund.',
  },
  jobEdit: {
    title: 'Redigera jobbet',
    fieldTitle: 'Titel',
    fieldDescription: 'Beskrivning',
    fieldPostalCode: 'Postnummer',
    invalidField: 'Kontrollera fältet',
    unknownCategory: 'Välj en kategori i listan',
    unknownPostalCode: 'Vi hittar inte det här postnumret',
    savedToast: 'Ändringarna är sparade.',
    conflict: 'Jobbet har ändrats. Ladda om sidan och försök igen.',
    saveFailed: 'Det gick inte att spara. Försök igen om en stund.',
  },
  client: {
    postJob: 'Lägg upp ett jobb',
    proposalCount: '{count} offerter',
    overview: {
      title: 'Hej {name}',
      description: 'Här ser du dina jobb och nya offerter.',
      activeJobs: 'Aktiva jobb',
      newProposals: 'Offerter att svara på',
      unreadMessages: 'Olästa meddelanden',
      needsAnswers: 'Jobb som väntar på dina svar',
      chosenJobs: 'Utdelade jobb',
    },
    jobs: {
      title: 'Mina jobb',
      description: 'Alla jobb du har lagt upp.',
      searchPlaceholder: 'Sök bland dina jobb',
      columns: {
        job: 'Jobb',
        category: 'Kategori',
        status: 'Status',
        proposals: 'Offerter',
        posted: 'Upplagt',
      },
      filters: {
        all: 'Alla',
        active: 'Aktiva',
        closed: 'Avslutade',
      },
      empty: 'Inga jobb än',
      emptyText: 'Jobb du lägger upp på hemmafixa.se hamnar här.',
      needsAnswer: 'Svara på frågan',
    },
    job: {
      closeJob: 'Avsluta jobbet',
      proposalsTitle: 'Offerter ({count})',
      noProposals: 'Inga offerter än',
      noProposalsText: 'Utförare i ditt område får jobbet så fort det är godkänt.',
      proposalsText:
        'Jämför offerterna. Acceptera en för att se kontaktuppgifter och chatta, och ge sedan jobbet till den du vill ha.',
      fixedPrice: 'Fast pris',
      priceRange: 'Prisintervall',
      startLabel: 'Start',
      durationLabel: 'Tid',
      includes: 'Ingår',
      viewProfile: 'Visa företaget',
      sentAt: 'Skickad {date}',
      contactHidden: 'Kontaktuppgifter visas när du har accepterat offerten.',
      declineTitle: 'Avböj offerten från {company}?',
      declineText: 'Utföraren får veta att du har avböjt. Det går inte att ångra.',
      declineReason: 'Anledning (frivilligt)',
      declineReasonHint: 'Visas för utföraren, till exempel ”För dyrt” eller ”Kan inte börja i tid”.',
      declineFailed: 'Det gick inte att avböja. Ladda om sidan och försök igen.',
      yourReason: 'Din anledning',
      otherProposals: 'Avböjda och stängda ({count})',
      declinedToast: 'Offerten är avböjd.',
      acceptTitle: 'Acceptera offerten från {company}?',
      acceptText:
        'Ni ser varandras kontaktuppgifter och en konversation öppnas. Du kan acceptera upp till {max} offerter på ett jobb och bestämma dig senare.',
      acceptedToast: 'Offerten är accepterad. Nu kan du kontakta företaget.',
      acceptFailed:
        'Det gick inte att acceptera. Jobbet kan ha stängt, eller så har du redan accepterat {max} offerter.',
      closeHow: 'Hur vill du avsluta det?',
      chooseProvider: 'Ge jobbet till {company}',
      cancelJob: 'Avbryt jobbet',
      cancelOnlyText: 'Acceptera företagets offert först om du vill ge dem jobbet.',
      cancelReason: 'Varför avbryter du? (valfritt)',
      cancelReasonHint: 'Bara Hemmafixa ser det här.',
      chosenToast: 'Klart! Företaget har fått besked och er konversation är fortsatt öppen.',
      cancelledToast: 'Jobbet är avbrutet.',
      closeFailed: 'Det gick inte att avsluta jobbet. Ladda om sidan och försök igen.',
      closeTitle: 'Avsluta jobbet?',
      closeText: 'Jobbet tas bort från jobblistan och utförarna får besked. Det går inte att ångra.',
      closedToast: 'Jobbet är avslutat.',
      verified: 'Verifierad utförare',
      startFrom: 'Kan börja {date}',
      duration: 'Cirka {days} dagar',
      accept: 'Acceptera',
      decline: 'Avböj',
      edit: 'Redigera jobbet',
      editText: 'Du kan ändra jobbet tills det är godkänt.',
      inReviewText: 'Hemmafixa granskar jobbet. Det syns för utförare när det är godkänt.',
      rejectedTitle: 'Jobbet godkändes inte',
      questionsTitle: 'Hemmafixa har en fråga',
      questionsText: 'Svara så granskar vi jobbet igen.',
      yourAnswer: 'Ditt svar',
      sendAnswers: 'Skicka svar',
      answerRequired: 'Skriv ett svar',
      answeredToast: 'Tack! Vi granskar jobbet igen.',
      answerFailed: 'Det gick inte att skicka svaret. Försök igen om en stund.',
      questions: 'Frågor och svar',
    },
  },
  provider: {
    overview: {
      title: 'Hej {name}',
      description: 'Här ser du var ditt företag står och vad nästa steg är.',
      newJobs: 'Nya matchande jobb',
      waiting: 'Offerter som väntar på svar',
      accepted: 'Accepterade: prata med kunden',
      savedJobs: 'Sparade jobb',
      won: 'Vunna jobb, senaste 30 dagarna',
      activeProposals: 'Aktiva offerter',
      acceptedCount: '{count} accepterade',
      unreadMessages: 'Olästa meddelanden',
      credits: 'Krediter kvar',
      renews: 'Förnyas {date}',
      latestJobs: 'Senaste jobben',
      viewProfile: 'Visa företagsprofilen',
      onboardingTitle: 'Gör klart din företagsprofil',
      onboardingText: 'Fyll i företaget, vad ni gör och var ni arbetar, så verifierar vi er.',
      onboardingAction: 'Fortsätt',
    },
    findJobs: {
      title: 'Hitta jobb',
      description: 'Godkända jobb i dina kategorier och områden som väntar på offerter.',
      searchPlaceholder: 'Sök på titel eller beskrivning',
      allCategories: 'Alla kategorier',
      empty: 'Inga öppna jobb just nu',
      emptyText: 'Nya jobb dyker upp här när Hemmafixa har godkänt dem.',
      quoted: 'Offert skickad',
      notVerifiedTitle: 'Ditt företag är inte verifierat än',
      notVerifiedText: 'När vi har verifierat företaget kan du se jobb och skicka offerter.',
      noPlanTitle: 'Välj ett abonnemang för att se jobb',
      noPlanText:
        'Med ett aktivt abonnemang får du jobb i dina kategorier och områden och kan skicka offerter.',
      unpaidTitle: 'Abonnemanget är obetalt',
      unpaidText:
        'Uppdatera ditt kort så ser du jobben och kan skicka offerter igen. Dina konversationer fortsätter.',
      toBilling: 'Till abonnemanget',
      toProfile: 'Till företagsprofilen',
      show: 'Visa',
      allJobs: 'Alla jobb',
      savedJobs: 'Sparade',
      anyStart: 'Alla starttider',
      yourProposal: 'Din offert',
      notQuoted: 'Ingen offert än',
      savedEmpty: 'Inga sparade jobb',
      savedEmptyText: 'Spara ett jobb med bokmärket så hittar du det här.',
      details: 'Jobbdetaljer',
      pickJob: 'Välj ett jobb i listan',
      openJob: 'Öppna jobbet',
      sendProposal: 'Skicka offert',
      proposals: 'Offerter hittills',
      save: 'Spara',
      unsave: 'Ta bort från sparade',
      savedToast: 'Jobbet är sparat',
      unsavedToast: 'Borttaget från sparade',
    },
    job: {
      yourProposal: 'Din offert',
      closedText: 'Jobbet är inte längre öppet.',
      priceType: 'Pris',
      fixedPrice: 'Fast pris',
      priceRange: 'Prisintervall',
      price: 'Pris (kr)',
      priceFrom: 'Pris från (kr)',
      priceTo: 'Pris till (kr)',
      priceHint: 'Ange vad kunden betalar, inklusive moms.',
      includes: 'Vad ingår',
      includesHint: 'Till exempel material, bortforsling och resor.',
      start: 'Tidigaste start',
      duration: 'Antal dagar',
      message: 'Meddelande till kunden',
      messageHint: 'Minst 20 tecken. Kontaktuppgifter döljs tills kunden accepterar offerten.',
      send: 'Skicka offert',
      saveChanges: 'Spara ändringar',
      sentToast: 'Offerten är skickad.',
      savedToast: 'Offerten är ändrad.',
      edit: 'Ändra offerten',
      withdraw: 'Återkalla',
      withdrawTitle: 'Återkalla offerten?',
      withdrawText: 'Kunden ser den inte längre, och du kan inte skicka en ny offert på det här jobbet.',
      withdrawnToast: 'Offerten är återkallad.',
      sentAt: 'Skickad {date}',
      declineReason: 'Kundens anledning',
      contactHidden: 'Kunden ser inte telefonnummer och e-postadresser i texten förrän offerten accepteras.',
      waitingText: 'Kunden har fått din offert. Du kan ändra den tills kunden svarar.',
      failed: 'Det gick inte att skicka. Försök igen om en stund.',
      conflict: 'Offerten eller jobbet har ändrats. Ladda om sidan.',
      sendProposal: 'Skicka offert',
      viewProposal: 'Din offert',
      proposals: 'Offerter',
      proposalsSoFar: '{count} offerter hittills',
      errors: {
        priceFromOre: 'Ange ett pris i hela kronor, minst 1 kr.',
        priceToOre: 'Måste vara högre än lägsta priset.',
        earliestStart: 'Välj ett giltigt datum.',
        durationDays: 'Ange 1–365 dagar.',
        message: 'Skriv minst 20 tecken.',
        includes: 'Högst 1 000 tecken.',
      },
    },
    proposals: {
      title: 'Mina offerter',
      description: 'Alla offerter du har skickat.',
      show: 'Visa',
      filters: { all: 'Alla', active: 'Pågående', closed: 'Avslutade' },
      columns: {
        job: 'Jobb',
        price: 'Pris',
        status: 'Status',
        sent: 'Skickad',
      },
      empty: 'Inga offerter här',
      emptyText: 'Hitta ett jobb och skicka din första offert.',
      findJobs: 'Hitta jobb',
    },
    onboarding: {
      title: 'Företagsprofil',
      description: 'Fyll i uppgifterna så kan vi verifiera ditt företag.',
      descriptionVerified: 'Det kunder ser om ditt företag.',
      progress: 'Steg {current} av {total}',
      steps: {
        company: 'Företag',
        services: 'Tjänster och områden',
        documents: 'Dokument',
        review: 'Granska och skicka',
      },
      stepText: [
        'Visas på din offentliga profil.',
        'Du får jobb i de kategorier och län du väljer.',
        'Vi behöver registreringsbevis och F-skattsedel innan din första offert.',
        'Kontrollera uppgifterna innan du skickar dem.',
      ],
      companyName: 'Företagsnamn',
      orgNumber: 'Organisationsnummer',
      orgNumberHint: '10 siffror, till exempel 556123-4567. Enskild firma: ditt personnummer.',
      about: 'Om företaget',
      aboutHint: 'Vad ni gör och vad ni är bra på. Visas för kunder.',
      website: 'Webbplats',
      fSkatt: 'Företaget har F-skatt',
      fSkattHint: 'Krävs för att ta uppdrag via Hemmafixa.',
      selectAll: 'Välj alla',
      clearAll: 'Rensa alla',
      showServices: 'Visa tjänster inom {name}',
      selectedCount: '{count}/{total}',
      categories: 'Kategorier',
      counties: 'Län',
      documentsTitle: 'Dokument',
      documentsText:
        'Ladda upp registreringsbevis och F-skattsedel, så går granskningen fortare. Du kan också skicka dem senare.',
      documentsVerifiedText: 'Byt ut ett dokument när det ändras, till exempel ett nytt registreringsbevis.',
      docTypes: {
        company_registration: 'Registreringsbevis',
        f_skatt: 'F-skattsedel',
        other: 'Övriga dokument',
      },
      docTypeHints: {
        f_skatt: 'Bevis från Skatteverket om att företaget har F-skatt.',
        company_registration: 'Registreringsbevis från Bolagsverket, högst 3 månader gammalt.',
        other: 'Till exempel behörighet, certifikat eller auktorisation.',
      },
      docDrop: 'Dra hit {document}',
      docHint: 'PDF, JPG eller PNG, upp till 10 MB.',
      submit: 'Skicka för granskning',
      doneToast: 'Skickat för granskning. Vi meddelar dig via e-post.',
      savedToast: 'Sparat.',
      saveFailed: 'Det gick inte att spara. Försök igen om en stund.',
      invalidField: 'Kontrollera fältet',
      orgNumberInvalid: 'Kontrollera organisationsnumret, det stämmer inte',
      orgNumberTaken: 'Ett annat konto använder redan det här organisationsnumret',
      chooseCategory: 'Välj minst en kategori',
      chooseCounty: 'Välj minst ett län',
      edit: 'Ändra',
      notSet: 'Inte ifyllt',
      pendingTitle: 'Vi granskar ditt företag',
      pendingText:
        'Vi kontrollerar organisationsnummer och F-skatt, oftast inom en arbetsdag. Du får besked via e-post.',
      verifiedTitle: 'Ditt företag är verifierat',
      verifiedText: 'Du kan välja abonnemang och börja skicka offerter.',
      verifiedEditText:
        'Du kan ändra beskrivning, webbplats, kategorier och län här. Företagsnamn, organisationsnummer och F-skatt har vi kontrollerat; vill du ändra dem, mejla hej@hemmafixa.se.',
      checkedDetails: 'Kontrollerade uppgifter',
      publicDetails: 'Visas för kunder',
      viewPublic: 'Visa din offentliga profil',
      saveDetails: 'Spara beskrivningen',
      saveServices: 'Spara tjänster och områden',
      servicesHint: 'Du får jobb i de kategorier och län du väljer.',
      websiteInvalid: 'Ange en webbadress, t.ex. www.exempel.se',
      rejectedTitle: 'Vi behöver fler uppgifter',
      rejectedText: 'Rätta uppgifterna nedan och skicka igen.',
    },
    subscription: {
      title: 'Abonnemang',
      description: 'Ditt abonnemang, krediter för offerter och fakturor.',
      creditsLeft: 'Krediter kvar denna period',
      creditsHint:
        'En kredit per offert. Oanvända krediter från abonnemanget nollställs varje period, extrapaket finns kvar.',
      buyPack: 'Köp {credits} krediter för {price}',
      packSuccess: 'Tack! Krediterna läggs till så snart Stripe har bekräftat betalningen.',
      packCancel: 'Köpet avbröts. Inget har dragits.',
      renews: 'Nästa förnyelse',
      trialEnds: 'Provperioden slutar',
      endsOn: 'Avslutas',
      current: 'Nuvarande',
      choose: 'Välj',
      startTrial: 'Prova gratis i {days} dagar',
      perMonth: '/ månad',
      proposalsPerMonth: '{count} offerter i månaden',
      allCategories: 'Alla kategorier och hela Sverige',
      vatNote: 'Priserna är exklusive moms. Betalning sker via Stripe.',
      plansTitle: 'Välj abonnemang',
      currentTitle: 'Ditt abonnemang',
      noSubscription: 'Du har inget abonnemang än. Välj ett för att skicka offerter.',
      manage: 'Hantera abonnemang',
      manageHint: 'Byt abonnemang, kort eller avsluta hos Stripe.',
      changeInPortal: 'Byt via Hantera abonnemang',
      pastDue:
        'Betalningen misslyckades. Uppdatera ditt kort före {date} så att du kan fortsätta skicka offerter.',
      canceling: 'Abonnemanget avslutas {date}.',
      billingOff: 'Betalning är inte igång än. Du kan se abonnemangen men inte välja ett.',
      checkoutSuccess: 'Tack! Ditt abonnemang startas så snart Stripe har bekräftat betalningen.',
      checkoutCancel: 'Betalningen avbröts. Inget har dragits.',
      invoicesTitle: 'Fakturor',
      noInvoices: 'Inga fakturor än.',
      invoiceNumber: 'Nummer',
      invoicePeriod: 'Period',
      invoiceTotal: 'Belopp inkl. moms',
      invoiceStatus: 'Status',
      viewInvoice: 'Visa',
      downloadPdf: 'PDF',
    },
  },
  charts: {
    lastSixMonths: 'Senaste 6 månaderna',
    proposalsSentWon: 'Skickade och vunna offerter',
    sent: 'Skickade',
    won: 'Vunna',
    yourProposals: 'Dina offerter',
    proposals: 'offerter',
    jobsAndProposals: 'Dina jobb och offerter',
    jobs: 'Publicerade jobb',
    proposalsReceived: 'Mottagna offerter',
    yourJobs: 'Dina jobb',
    jobsCenter: 'jobb',
    platformActivity: 'Aktivitet på plattformen',
    newJobs: 'Nya jobb',
    newUsers: 'Nya användare',
    newProposals: 'Offerter',
    providersByStatus: 'Företag',
    companies: 'företag',
    other: 'Övriga',
    inReview: 'Granskas',
    aiBudget: 'AI-budget denna månad',
    winRate: 'Vinstandel',
    winRateText: '{won} av {total} offerter vunna',
    active: 'Aktiva',
    unread: 'Olästa',
    open: 'Öppna',
    thisMonth: 'denna månad',
  },
  dashboard: {
    postJobTitle: 'Behöver du mer hjälp hemma?',
    postJobText: 'Lägg upp ett nytt jobb och få offerter från verifierade företag, oftast inom ett dygn.',
    myJobs: 'Mina jobb',
    reviewTitle: 'Jobb som väntar på granskning',
    reviewText: 'Godkänn, fråga kunden eller avvisa. Varje jobb granskas innan företagen ser det.',
    openQueue: 'Öppna kön',
  },
  overview: {
    needsAttention: 'Behöver din uppmärksamhet',
    nothingWaiting: 'Inget väntar på dig just nu.',
    recentActivity: 'Senaste händelser',
    noActivity: 'Inget har hänt än.',
    compareProposals: 'Nya offerter på ”{title}”: {count}',
    compareProposalsText: 'Jämför pris och start och acceptera de du vill prata med.',
    compare: 'Jämför',
    chooseProvider: 'Överens om ”{title}”? Ge jobbet till företaget',
    chooseProviderText: 'Accepterade: {names}. Välj ett under Avsluta jobbet när ni är överens.',
    choose: 'Välj',
    answerQuestions: 'Hemmafixa har frågor om ”{title}”',
    answerQuestionsText: 'Svara så att jobbet kan publiceras.',
    answer: 'Svara',
    unreadMessages: 'Olästa meddelanden från {name}: {count}',
    openChat: 'Öppna chatten',
    talkToClient: '{client} har accepterat din offert på ”{title}”',
    talkToClientText: 'Kom överens om detaljerna; kunden ger dig jobbet när ni är klara.',
    newJobs: 'Nya jobb som passar ditt företag: {count}',
    newJobsText: 'Jobb i dina kategorier och områden som du inte har lämnat offert på.',
    findJobs: 'Hitta jobb',
    reviewJobs: 'Jobb som väntar på granskning: {count}',
    verifyProviders: 'Företag som väntar på verifiering: {count}',
    handleReports: 'Öppna anmälningar: {count}',
    review: 'Granska',
    handle: 'Hantera',
  },
  report: {
    button: 'Anmäl',
    title: {
      message: 'Anmäl meddelandet',
      proposal: 'Anmäl offerten',
      provider: 'Anmäl företaget',
      user: 'Anmäl personen',
    },
    text: 'Hemmafixa går igenom varje anmälan. Den andra parten får inte veta vem som anmälde.',
    reasonLabel: 'Vad är fel?',
    reasonRequired: 'Välj en anledning',
    reasons: {
      spam: 'Reklam eller skräp',
      abusive: 'Otrevligt eller kränkande',
      fraud: 'Ser ut som bedrägeri',
      off_platform: 'Vill flytta kontakt eller betalning utanför Hemmafixa',
      other: 'Något annat',
    },
    details: 'Mer information (valfritt)',
    detailsHint: 'Vad hände? Bara Hemmafixa läser det här.',
    send: 'Skicka anmälan',
    sentToast: 'Tack. Hemmafixa tittar på det.',
    alreadyReported: 'Du har redan anmält det här.',
    failed: 'Det gick inte att skicka anmälan. Försök igen om en stund.',
  },
  publicProfile: {
    title: 'Utförarens profil',
    preview: 'Så här ser kunder din profil.',
    verified: 'Verifierat företag',
    verifiedHint: 'Hemmafixa har kontrollerat organisationsnummer och F-skatt.',
    orgNumber: 'Org.nr',
    memberSince: 'Medlem sedan {date}',
    about: 'Om företaget',
    noAbout: 'Företaget har inte skrivit någon beskrivning än.',
    services: 'Tjänster',
    areas: 'Arbetar i',
    website: 'Webbplats',
    contactNote: 'Kontaktuppgifter delas när du har accepterat en offert från företaget.',
  },
  admin: {
    billing: {
      title: 'Abonnemang och intäkter',
      description: 'Abonnemang, månatliga intäkter, churn och planer.',
      tabs: { overview: 'Översikt', subscriptions: 'Abonnemang', plans: 'Planer' },
      mrr: 'Månadsintäkt (MRR)',
      mrrHint: 'Aktiva och obetalda, exkl. moms',
      active: 'Aktiva',
      trialing: 'Provperiod',
      pastDue: 'Betalning misslyckad',
      churnHint: 'Churn denna månad: {rate}',
      revenue: 'Intäkter per månad',
      revenueHint: 'Betalda fakturor exkl. moms, senaste 12 månaderna',
      startedChurned: 'Nya och avslutade abonnemang',
      started: 'Nya',
      churned: 'Avslutade',
      searchPlaceholder: 'Sök företag, org.nr eller e-post',
      allStatuses: 'Alla',
      empty: 'Inga abonnemang än.',
      columns: {
        company: 'Företag',
        plan: 'Plan',
        price: 'Pris / mån',
        status: 'Status',
        renews: 'Nästa datum',
        started: 'Skapad',
      },
      endsOn: 'Slutar {date}',
      trialUntil: 'Prov till {date}',
      graceUntil: 'Betala före {date}',
      noCompany: 'Företagsnamn saknas',
      subscribers: '{count} prenumeranter',
      inactive: 'Dold för nya',
      notInStripe: 'Skapas i Stripe vid första köpet',
      edit: 'Redigera',
      editTitle: 'Redigera {name}',
      priceNote: 'Ett nytt pris gäller nya prenumeranter. Nuvarande behåller sitt pris.',
      fields: {
        nameSv: 'Namn (svenska)',
        nameEn: 'Namn (engelska)',
        descriptionSv: 'Beskrivning (svenska)',
        descriptionEn: 'Beskrivning (engelska)',
        price: 'Pris per månad, kr exkl. moms',
        proposals: 'Offerter per månad',
        trialDays: 'Dagar provperiod',
        position: 'Ordning',
        active: 'Visas för nya företag',
      },
      saved: 'Planen är sparad.',
      invalid: 'Kontrollera fälten.',
    },
    overview: {
      title: 'Översikt',
      description: 'Det som väntar på dig idag.',
      jobsToReview: 'Jobb att granska',
      providersToVerify: 'Utförare att verifiera',
      openReports: 'Öppna anmälningar',
      openJobs: 'Öppna jobb',
      lastWeek: 'Senaste 7 dagarna',
      latestActions: 'Senaste admin-åtgärder',
      newJobs: 'Nya jobb',
      newUsers: 'Nya konton',
      newProposals: 'Skickade offerter',
      subscriptions: 'Aktiva abonnemang',
      aiSpend: 'AI-kostnad denna månad',
      aiBudget: 'Budget {budget}',
    },
    review: {
      details: 'Jobbdetaljer',
      title: 'Granska jobb',
      description: 'Nya jobb syns inte för utförare förrän du godkänner dem.',
      columns: {
        job: 'Jobb',
        category: 'Kategori',
        client: 'Kund',
        location: 'Postnummer',
        status: 'Status',
        received: 'Inkom',
      },
      searchPlaceholder: 'Sök på titel, kund eller e-post',
      filters: { all: 'Alla', in_review: 'Att granska', needs_info: 'Väntar på kunden' },
      empty: 'Inga jobb att granska',
      emptyText: 'Nya jobb hamnar här.',
      approve: 'Godkänn',
      ask: 'Fråga kunden',
      reject: 'Avvisa',
      edit: 'Redigera',
      editText: 'Rätta stavning eller förtydliga innan du godkänner. Ändringen loggas.',
      approveTitle: 'Godkänna jobbet?',
      approveText: 'Jobbet publiceras och matchande utförare får det direkt.',
      approvedToast: 'Jobbet är godkänt och skickas till matchande utförare.',
      askTitle: 'Fråga kunden',
      askText: 'Kunden får frågorna via e-post och svarar i sin översikt.',
      rejectTitle: 'Avvisa jobbet',
      rejectText: 'Kunden får anledningen via e-post.',
      question: 'Fråga {number}',
      addQuestion: 'Lägg till fråga',
      removeQuestion: 'Ta bort fråga {number}',
      reason: 'Anledning',
      sendQuestion: 'Skicka frågorna',
      askedToast: 'Frågorna är skickade till kunden.',
      rejectedToast: 'Jobbet är avvisat.',
      waitingForClient: 'Väntar på kundens svar. Du kan fortfarande redigera eller avvisa jobbet.',
      reviewed: 'Jobbet är redan granskat.',
      rejectionReason: 'Anledning till avvisning',
      contact: 'Kontakt',
      email: 'E-post',
      phone: 'Telefon',
      accountSuspended: 'Kontot är avstängt',
      questions: 'Frågor och svar',
      history: 'Historik',
      system: 'Systemet',
      questionTooShort: 'Skriv minst 5 tecken',
      reasonTooShort: 'Skriv minst 3 tecken',
      conflict: 'Jobbet har ändrats av någon annan. Ladda om sidan och försök igen.',
      genericError: 'Det gick inte att spara. Försök igen om en stund.',
    },
    providers: {
      noDocuments: 'Inga dokument uppladdade.',
      title: 'Utförare',
      description: 'Verifiera nya företag innan de kan skicka offerter.',
      searchPlaceholder: 'Sök på företag, org.nr, namn eller e-post',
      columns: {
        company: 'Företag',
        orgNumber: 'Org.nr',
        contact: 'Kontaktperson',
        categories: 'Kategorier',
        counties: 'Län',
        status: 'Status',
        submitted: 'Inskickad',
      },
      noCompanyName: 'Inget företagsnamn än',
      empty: 'Inga företag väntar',
      emptyText: 'Företag hamnar här när de har skickat in sin profil.',
      verify: 'Verifiera',
      reject: 'Avvisa',
      verifyTitle: 'Verifiera företaget?',
      verifyText:
        'Kontrollera organisationsnumret och F-skatten hos Skatteverket innan du verifierar. Företaget får ett mejl.',
      rejectTitle: 'Avvisa företaget',
      rejectText: 'Utföraren får anledningen via e-post och kan rätta profilen och skicka den igen.',
      reason: 'Anledning',
      reasonTooShort: 'Skriv minst 3 tecken',
      verifiedToast: 'Företaget är verifierat.',
      rejectedToast: 'Företaget är avvisat.',
      company: 'Företaget',
      fSkatt: 'F-skatt',
      fSkattConfirmed: 'Ja, enligt utföraren',
      fSkattMissing: 'Nej',
      about: 'Om företaget',
      website: 'Webbplats',
      services: 'Tjänster och områden',
      categories: 'Kategorier',
      counties: 'Län',
      contact: 'Kontaktperson',
      email: 'E-post',
      phone: 'Telefon',
      openAccount: 'Visa kontot',
      accountSuspended: 'Kontot är avstängt',
      submitted: 'Inskickad',
      verifiedAt: 'Verifierad',
      rejectionReason: 'Anledning till avvisning',
      notSubmitted: 'Utföraren har inte skickat in profilen än.',
      decided: 'Profilen är redan granskad.',
      waitingAgain: 'Utföraren kan rätta profilen och skicka den igen.',
      history: 'Historik',
      activity: 'Händelser',
      system: 'Systemet',
      conflict: 'Profilen har ändrats av någon annan. Ladda om sidan och försök igen.',
      genericError: 'Det gick inte att spara. Försök igen om en stund.',
    },
    users: {
      title: 'Användare',
      description: 'Alla konton på plattformen.',
      searchPlaceholder: 'Sök på namn eller e-post',
      columns: {
        name: 'Namn',
        email: 'E-post',
        roles: 'Roller',
        status: 'Status',
        lastLogin: 'Senast inloggad',
      },
      allRoles: 'Alla roller',
      allStatuses: 'Alla',
      neverLoggedIn: 'Aldrig',
      empty: 'Inga konton än',
      account: 'Konto',
      phone: 'Telefon',
      language: 'Språk',
      languages: { sv: 'Svenska', en: 'Engelska' },
      created: 'Skapat',
      password: 'Lösenord',
      passwordSet: 'Valt',
      passwordNotSet: 'Inte valt än',
      sessions: 'Inloggade enheter',
      jobs: 'Skickade jobb',
      permissions: 'Behörigheter',
      permissionNames: { finance: 'Ekonomi', moderation: 'Moderering', support: 'Support' },
      company: 'Företag',
      openProvider: 'Visa företaget',
      suspend: 'Stäng av kontot',
      reactivate: 'Aktivera kontot',
      suspendTitle: 'Stänga av kontot?',
      suspendText:
        'Personen loggas ut direkt och kan inte logga in förrän kontot aktiveras igen. Anledningen sparas i loggen och visas inte för personen.',
      reason: 'Anledning',
      reasonTooShort: 'Skriv minst 3 tecken',
      reactivateTitle: 'Aktivera kontot?',
      reactivateText: 'Personen kan logga in igen.',
      note: 'Anteckning (frivillig)',
      suspendedToast: 'Kontot är avstängt.',
      reactivatedToast: 'Kontot är aktivt igen.',
      contact: 'Kontakt',
      ownAccount: 'Det här är ditt eget konto.',
      suspendedNotice: 'Kontot är avstängt och kan inte logga in.',
      activity: 'Händelser',
      conflict: 'Kontot har ändrats av någon annan. Ladda om sidan och försök igen.',
      genericError: 'Det gick inte att spara. Försök igen om en stund.',
    },
    audit: {
      title: 'Händelselogg',
      description: 'Vem ändrade vad, och när.',
      searchPlaceholder: 'Sök på person eller händelse, t.ex. provider.',
      columns: { when: 'När', who: 'Vem', what: 'Händelse' },
      filters: { all: 'Alla', user: 'Konton', provider: 'Utförare', job: 'Jobb', report: 'Anmälningar' },
      system: 'Systemet',
      empty: 'Inga händelser än',
      open: 'Öppna',
      actions: {
        'user.create_from_job': 'Konto skapat från jobbformuläret',
        'user.create_provider_signup': 'Konto skapat som utförare',
        'user.password_changed': 'Bytte lösenord',
        'user.password_set': 'Valde lösenord',
        'user.password_reset': 'Återställde lösenordet via länk',
        'user.profile_updated': 'Ändrade sina uppgifter',
        'user.suspend': 'Stängde av kontot',
        'user.reactivate': 'Aktiverade kontot',
        'provider.start': 'Började som utförare',
        'provider.verify': 'Verifierade företaget',
        'provider.reject': 'Avvisade företaget',
        'provider.profile_updated': 'Ändrade företagsbeskrivningen',
        'provider.services_updated': 'Ändrade tjänster och områden',
        'report.resolve': 'Åtgärdade en anmälan',
        'report.dismiss': 'Avfärdade en anmälan',
        'job.edit': 'Redigerade jobbet',
      } as Record<string, string>,
    },
    reports: {
      title: 'Anmälningar',
      description: 'Anmälda meddelanden, offerter, företag och personer.',
      empty: 'Inga anmälningar här',
      emptyText: 'Anmälningar från kunder och utförare visas här.',
      columns: {
        what: 'Vad',
        reason: 'Anledning',
        summary: 'Anmält',
        subject: 'Gäller',
        reported: 'Anmäld',
        status: 'Status',
      },
      targets: { message: 'Meddelande', proposal: 'Offert', provider: 'Företag', user: 'Person' },
      statuses: { open: 'Öppna', resolved: 'Åtgärdade', dismissed: 'Avfärdade' },
      reporter: 'Anmäld av',
      deleted: 'Borttaget konto',
      otherReports: 'Andra anmälningar om dem',
      reported: 'Det som anmäldes',
      reporterText: 'Vad anmälaren skrev',
      hidden: 'Dolt',
      blocked: 'Konversationen stoppad',
      decision: 'Beslut',
      chooseAction: 'Vad vill du göra?',
      actions: {
        warned: 'Varna',
        message_hidden: 'Dölj meddelandet',
        conversation_blocked: 'Stoppa konversationen',
        account_suspended: 'Stäng av kontot',
      },
      actionTexts: {
        warned: 'De får en påminnelse om reglerna som avisering och mejl, utan din anteckning.',
        message_hidden: 'Ingen av parterna ser texten eller filerna längre.',
        conversation_blocked: 'Båda kan läsa, ingen kan skriva.',
        account_suspended: 'Loggas ut direkt och kan inte logga in. Anteckningen blir anledningen.',
      },
      dismiss: 'Avfärda',
      dismissText: 'Inget fel; stäng anmälan utan åtgärd.',
      confirm: '{action}',
      note: 'Anteckning',
      noteOptional: 'Anteckning (valfritt)',
      noteHint: 'Sparas med anmälan och i händelseloggen. Visas inte för någon annan.',
      noteRequired: 'Skriv minst 3 tecken',
      action: 'Åtgärd',
      handledBy: 'Hanterad av',
      handledAt: 'Hanterad',
      resolvedToast: 'Klart. Anmälan är åtgärdad.',
      dismissedToast: 'Anmälan är avfärdad.',
      conflict: 'Någon annan har redan hanterat anmälan. Ladda om sidan.',
      failed: 'Det gick inte att spara. Försök igen om en stund.',
    },
  },
  messages: {
    title: 'Meddelanden',
    description: 'En konversation öppnas när kunden accepterar en offert.',
    empty: 'Inga konversationer än',
    emptyText: 'När en offert accepteras kan ni skriva till varandra här.',
    pick: 'Välj en konversation',
    placeholder: 'Skriv ett meddelande',
    send: 'Skicka',
    attach: 'Lägg till bilder eller PDF',
    open: 'Öppna chatten',
    noMessages: 'Inga meddelanden än',
    files: '{count} fil(er)',
    you: 'Du: {text}',
    unread: '{count} olästa',
    older: 'Visa äldre meddelanden',
    start: 'Säg hej och kom överens om detaljerna.',
    hidden: 'Meddelandet har tagits bort av Hemmafixa.',
    closed: 'Jobbet är stängt. Du kan läsa konversationen men inte skriva.',
    blocked: 'Hemmafixa har stoppat den här konversationen.',
    sendFailed: 'Det gick inte att skicka. Kontrollera anslutningen och försök igen.',
    fileFailed: '{name} kunde inte laddas upp',
  },
  files: {
    title: 'Bilder och dokument',
    hint: 'JPG, PNG eller WebP upp till 10 MB, PDF upp till 15 MB. Högst {max} filer.',
    drop: 'Dra hit bilder eller dokument',
    choose: 'Välj filer',
    uploading: 'Laddar upp …',
    preparing: 'Förbereder bilden …',
    failed: 'Uppladdningen misslyckades',
    wrongFile: 'Filen är för stor eller av fel typ',
    remove: 'Ta bort {name}',
    removed: 'Filen är borttagen.',
    removeFailed: 'Det gick inte att ta bort filen.',
    typeNotAllowed: '{name}: den här filtypen går inte att ladda upp.',
    tooLarge: '{name}: större än {limit}.',
    tooMany: 'Du kan lägga till högst {max} filer.',
    none: 'Inga bilder eller dokument.',
    locked: 'Bilderna och dokumenten kan inte ändras när jobbet är godkänt.',
  },
  notifications: {
    title: 'Aviseringar',
    description: 'Det som har hänt med dina jobb och offerter.',
    newJobMatch: 'Nytt jobb i ditt område',
    newProposal: 'Ny offert från {provider}',
    proposalAccepted: '{client} har accepterat din offert',
    newMessage: 'Nytt meddelande från {sender}',
    proposalChosen: '{client} valde dig för jobbet',
    jobClosedOther: 'Kunden valde en annan utförare',
    jobClosedCancelled: 'Kunden avbröt jobbet',
    moderationWarning: 'En påminnelse om våra regler',
    moderationWarningText:
      'Vi har fått en anmälan om ditt konto. Följ reglerna så att alla kan känna sig trygga.',
    trialEnding: 'Din provperiod slutar {date}',
    trialEndingText: 'Sedan dras abonnemanget från ditt kort varje månad.',
    paymentFailed: 'Betalningen för abonnemanget misslyckades',
    paymentFailedText: 'Uppdatera ditt kort före {date} för att fortsätta skicka offerter.',
    accessPaused: 'Du kan inte skicka offerter just nu',
    accessPausedText: 'Abonnemanget är obetalt. Dina konversationer fortsätter som vanligt.',
    jobExpiring: 'Ditt jobb stänger snart',
    jobNoProposals: 'Inga offerter än på ditt jobb',
    markAllRead: 'Markera alla som lästa',
    viewAll: 'Visa alla aviseringar',
    empty: 'Inga aviseringar än',
    emptyText: 'Här dyker det upp när något händer, till exempel en ny offert eller ett nytt jobb.',
    noUnread: 'Inga olästa aviseringar',
    unread: 'oläst',
    show: 'Visa',
    all: 'Alla',
    unreadFilter: 'Olästa',
    settingsTitle: 'Aviseringar',
    settingsText: 'Du ser alltid aviseringar här i appen. Välj om du också vill få dem via e-post och sms.',
    email: 'E-post',
    sms: 'Sms',
    smsNeedsPhone: 'Lägg till ett telefonnummer i profilen för att få sms.',
    savedToast: 'Sparat.',
    saveFailed: 'Det gick inte att spara. Försök igen om en stund.',
  },
  settings: {
    title: 'Inställningar',
    description: 'Dina kontouppgifter, aviseringar och ditt lösenord.',
    profile: 'Profil',
    name: 'Namn',
    nameInvalid: 'Ange ditt namn (2–100 tecken).',
    phone: 'Telefon',
    phoneHint: 'Valfritt.',
    phoneInvalid: 'Ange ett giltigt telefonnummer, t.ex. 070-123 45 67.',
    email: 'E-post',
    emailHint: 'Vi skickar inloggningslänkar och aviseringar hit. Kontakta oss om du behöver byta.',
    savedToast: 'Profilen är sparad.',
    password: 'Lösenord',
    passwordText: 'Andra enheter loggas ut när du byter lösenord. Den här enheten förblir inloggad.',
    currentPassword: 'Nuvarande lösenord',
    wrongCurrentPassword: 'Det nuvarande lösenordet stämmer inte.',
    changePassword: 'Byt lösenord',
    passwordChanged: 'Lösenordet är bytt. Andra enheter har loggats ut.',
  },
  system: {
    title: 'System',
    description: 'Status för API:t och tjänsterna det använder.',
    status: 'Systemstatus',
    versions: 'API-version {api} · webbversion {web}',
    up: 'Igång',
    down: 'Nås inte',
    unreachable: 'API:t går inte att nå',
    unreachableText: 'Webbappen kunde inte nå {url}. Starta API:t eller kontrollera adressen i VITE_API_URL.',
    services: {
      database: 'Databas',
      redis: 'Köer och begränsningar',
      storage: 'Fillagring',
    },
  },
};

export type Messages = typeof sv;
